/**
 * Map accepted canonical page-text match offsets back to PDF.js text-item spans.
 */

import type { PdfTextItemCharSource } from "./cognitoryPdfPageTextCanonicalization";

export type PdfTextLayerHighlightSpan = {
  itemIndex: number;
  charStart: number;
  charEnd: number;
};

export type PdfTextLayerHighlightRange = {
  spans: readonly PdfTextLayerHighlightSpan[];
};

export function mapCanonicalMatchRangeToTextLayerHighlights(input: {
  charToItem: ReadonlyArray<PdfTextItemCharSource | null>;
  start: number;
  end: number;
}): PdfTextLayerHighlightRange {
  const spanByItem = new Map<number, PdfTextLayerHighlightSpan>();

  for (
    let canonicalIndex = input.start;
    canonicalIndex < input.end;
    canonicalIndex++
  ) {
    const source = input.charToItem[canonicalIndex];
    if (!source) {
      continue;
    }

    const existing = spanByItem.get(source.itemIndex);
    if (!existing) {
      spanByItem.set(source.itemIndex, {
        itemIndex: source.itemIndex,
        charStart: source.charIndex,
        charEnd: source.charIndex + 1,
      });
      continue;
    }

    existing.charStart = Math.min(existing.charStart, source.charIndex);
    existing.charEnd = Math.max(existing.charEnd, source.charIndex + 1);
  }

  return {
    spans: [...spanByItem.values()].sort(
      (left, right) => left.itemIndex - right.itemIndex,
    ),
  };
}

export type PdfTextLayerItemHighlightSlice = {
  before: string;
  highlighted: string;
  after: string;
};

export function resolvePdfTextLayerItemHighlightSlice(input: {
  itemText: string;
  span: PdfTextLayerHighlightSpan | undefined;
}): PdfTextLayerItemHighlightSlice {
  if (!input.span) {
    return {
      before: input.itemText,
      highlighted: "",
      after: "",
    };
  }

  const charStart = Math.max(
    0,
    Math.min(input.span.charStart, input.itemText.length),
  );
  const charEnd = Math.max(
    charStart,
    Math.min(input.span.charEnd, input.itemText.length),
  );

  return {
    before: input.itemText.slice(0, charStart),
    highlighted: input.itemText.slice(charStart, charEnd),
    after: input.itemText.slice(charEnd),
  };
}
